import { ITask } from '@/interfaces/Task';
import styled from '@emotion/styled';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Fade,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { Alert, ManageTasksEvent, Theme } from 'klayowebshared';
import { concat, debounce, get, isEmpty, isEqual } from 'lodash';
import React, { Component, SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import { track } from '../../common/track';
import { Snackbar } from '../../components/Snackbar';
import { TextField } from '../../components/TextField';
import { Tooltip } from '../../components/Tooltip';
import { Dialog } from '../../components/dialogs/Dialog';
import { MultiAttributeSelector } from '../../components/selectors/MultiAttributeSelector';
import { CREATE_TASK_MODE, PAGE_SIZE_FOR_ADD_EDIT } from '../../constants';
import { Data } from '../../data/Data';
import { SettingsAttributeList } from '../../data/settings/SettingsAttributeList';
import type { CurrentUser } from '../../data/users/CurrentUser';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import RemoveIcons from '../../resources/images/icons-remove.svg';
import type {
  ActionTask,
  CreateTaskDialogState,
  ImportedTask,
  TaskAttribute,
  TaskSuggestion,
} from '../../types/views/createTask.types';
import ImportTaskDetailDialog from './ImportTaskDetailDialog';
import ImportTasksErrorDialog from './ImportTasksErrorDialog';

interface CreateTaskDialogProps extends WithLocaleProps {
  theme?: typeof Theme;
  user?: CurrentUser | null;
  mode?: string;
  newTaskName?: string;
  actionTask?: ActionTask | null;
  error?: string;
  hasNewItem?: boolean;
  onClose?: () => void;
  onSave?: (taskName: string, attributes: TaskAttribute[]) => void;
  onShowNewAttributeDialog?: () => void;
  onRefreshTasks?: () => void;
  onImportedTasks?: (tasks: ImportedTask[]) => void;
  [key: string]: unknown;
}

class CreateTaskDialogBase extends Component<CreateTaskDialogProps, CreateTaskDialogState> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  private debouncedSearchTaskSuggestions: ReturnType<typeof debounce>;

  constructor(props: CreateTaskDialogProps) {
    super(props);

    this.state = {
      attributes: null,
      selectedSuggestedAttributes: [],
      allSelectedAttributes: [],
      suggestedAttributes: [],
      attributesSearch: null,
      selectedAttributes: [],
      taskName: null,
      originalTaskName: null,
      originalSelectedAttributes: null,
      isAttributeLoading: false,
      taskSuggestions: [],
      isLoadingTaskSuggestions: false,
      isTaskFromSuggestion: false,
      isOpenImportTaskDialog: false,
      showMessage: false,
      cacheRows: {
        importedTasksCount: 0,
        notImportedTasksCount: 0,
      },
      isLoadingImportTask: false,
      showImportTasksError: false,
    };

    this.onSearchAttributes = debounce(this.onSearchAttributes.bind(this), 500);
    this.debouncedSearchTaskSuggestions = debounce(this.loadTaskSuggestions.bind(this), 500);
  }

  componentDidMount() {
    const { mode, newTaskName } = this.props;
    this.loadAttributes();
    if (mode === (CREATE_TASK_MODE.EDIT as any)) {
      this.loadTaskDetail();
    } else {
      this.loadTaskSuggestions();
    }
    if (newTaskName) {
      this.setState({ taskName: newTaskName });
    }
  }

  loadTaskSuggestions(searchText?: string): void {
    if (!this.context.organization?.enableStandardsLibrary) return;

    if (searchText) track(ManageTasksEvent.NewTaskEmptySearch, { search: searchText });

    this.setState({ isLoadingTaskSuggestions: true });

    Data.call('get', `/StandardLibrary/tasks`, {
      params: {
        pageNumber: 1,
        pageSize: 5,
        searchText: (searchText || '').trim(),
        excludeExisting: true,
        showExtra: false,
      },
    })
      .then((response) => {
        const tasks = get(response, 'data.standardLibraryTasks.data', []);
        const suggestions: TaskSuggestion[] = tasks.map((task: { id: string; name: string }) => {
          return {
            id: task.id,
            name: task.name,
          };
        });
        this.setState({ taskSuggestions: suggestions });
      })
      .catch((error: Error) => {
        console.error('Error loading task suggestions:', error);
      })
      .finally(() => {
        this.setState({ isLoadingTaskSuggestions: false });
      });
  }

  loadTaskDetail(): void {
    const { actionTask } = this.props;
    if (!actionTask) return;

    Data.get<{ regulationTask: ITask }>(this.context, `/Regulation/Task/${actionTask.id}`)
      .then((response) => {
        this.setState({
          taskName: get(response, 'data.regulationTask.name', ''),
          originalTaskName: get(response, 'data.regulationTask.name', ''),
          originalSelectedAttributes: get(response, 'data.regulationTask.competencies', []),
        });
        this.setAttributes(get(response, 'data.regulationTask.competencies', []));
      })
      .catch((e: Error) => {})
      .finally(() => {
        this.context?.setLoading?.('tasks', false);
      });
  }

  loadAttributes(searchText?: string): void {
    this.setState({
      isAttributeLoading: true,
    });
    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    SettingsAttributeList.getWithPaging(this.context, pagingOptions)
      .then((attributes) => {
        this.setState({ attributes: attributes, attributesSearch: searchText || null });
      })
      .catch((error) => {})
      .finally(() =>
        this.setState({
          isAttributeLoading: false,
        })
      );
  }

  onAttributesBlur(): void {
    const { attributesSearch } = this.state;
    if (attributesSearch) this.loadAttributes();
  }

  onHandleSearchAttributes(searchText: string): void {
    this.setState({ attributes: null, isAttributeLoading: true });
    this.onSearchAttributes(searchText);
  }

  onSearchAttributes(searchText: string): void {
    this.loadAttributes(searchText);
  }

  onSave(): void {
    const { onSave } = this.props;
    const { taskName, allSelectedAttributes } = this.state;
    if (onSave && taskName) {
      track(ManageTasksEvent.NewTaskImport);
      onSave(taskName, allSelectedAttributes);
    }
  }

  onAttributesChange(e: SyntheticEvent | null, selectedAttributes: TaskAttribute[]): void {
    const { selectedSuggestedAttributes } = this.state;
    this.setAttributes([...selectedSuggestedAttributes, ...selectedAttributes]);
  }

  setAttributes(allSelectedAttributes: TaskAttribute[]): void {
    allSelectedAttributes.forEach((a: TaskAttribute) => (a.type = null));
    this.setState({ allSelectedAttributes, selectedAttributes: allSelectedAttributes });
  }

  isNameValid(name?: string | null): boolean {
    return !!name && name.length > 2 && name.length < 1000;
  }

  handleValidationText(name: string | null): string | undefined {
    const { translate } = this.props;
    if (!name || name.length < 3) return translate('settings.validationNameTooShort');
    if (!name || name.length > 1000) return translate('settings.validationNameTooLong');
  }

  isTaskNameAndAttributesChanged(): boolean {
    const { taskName, originalTaskName, allSelectedAttributes, originalSelectedAttributes } =
      this.state;
    if (!originalSelectedAttributes) return true;
    return (
      taskName !== originalTaskName ||
      !isEqual(
        allSelectedAttributes.map((a: TaskAttribute) => a.attributeDefinitionId),
        originalSelectedAttributes.map((a: TaskAttribute) => a.attributeDefinitionId)
      )
    );
  }

  onSelectSuggestion(e: SyntheticEvent, newValue: TaskSuggestion | string | null): void {
    if (typeof newValue === 'object' && newValue !== null) {
      track(ManageTasksEvent.NewTaskEmptySelectSuggestion);
      this.setState({ taskName: newValue.name, taskId: newValue.id, isOpenImportTaskDialog: true });
    } else if (typeof newValue === 'string') {
      this.setState({ taskName: newValue });
    }
  }

  onUpdateName(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    this.setState({ taskName: e.target.value });
  }

  onImportTaskSingle(taskId: string): void {
    track(ManageTasksEvent.NewTaskImport);
    this.setState({ isLoadingImportTask: true });
    Data.call('post', `/StandardLibrary/tasks/import`, { data: [taskId] })
      .then((response) => {
        this.setState({
          isOpenImportTaskDialog: false,
          showMessage: true,
          cacheRows: response.data,
        });

        const { notImportedTasks, failedToImportTasks, importedTasks } = response.data;

        if (!isEmpty(notImportedTasks) || !isEmpty(failedToImportTasks)) {
          return;
        }

        this.props.onRefreshTasks?.();

        this.props.onImportedTasks?.(importedTasks);
      })
      .catch((error: Error) => {})
      .finally(() => {
        this.setState({ isLoadingImportTask: false });
      });
  }

  onCloseImportTaskDialog() {
    this.setState({ isOpenImportTaskDialog: false });
  }

  setStateOfMessage(state: boolean) {
    this.setState({ showMessage: state });
  }

  onImportTasksError() {
    this.setState({
      showImportTasksError: true,
    });
    this.setStateOfMessage(false);
  }

  onCloseImportTasksError() {
    this.setState({
      showImportTasksError: false,
    });
  }

  get errorTasks() {
    const { cacheRows } = this.state;

    return concat(cacheRows.notImportedTasks || [], cacheRows.failedToImportTasks || []);
  }

  render() {
    const { user, theme, onClose, error, onShowNewAttributeDialog, mode, hasNewItem, translate } =
      this.props;
    const {
      attributes,
      selectedAttributes,
      taskName,
      taskSuggestions,
      isLoadingTaskSuggestions,
      showMessage,
      cacheRows,
    } = this.state;

    let attributesFiltered = get(attributes, 'attributes', []);

    if (get(selectedAttributes, 'length', 0) > 0) {
      attributesFiltered = attributesFiltered.filter(
        (attr: TaskAttribute) =>
          !selectedAttributes.some(
            (selected: TaskAttribute) =>
              get(selected, 'attributeDefinitionId') === get(attr, 'attributeId')
          )
      );
    }

    const { organization } = this.context;

    return (
      <Dialog
        open={true}
        theme={theme}
        onClose={onClose}
        fullWidth={true}
        maxWidth='sm'
        title={
          mode === (CREATE_TASK_MODE.EDIT as any)
            ? translate('settings.editTask')
            : translate('settings.createTask')
        }
        actions={[
          {
            label:
              mode === (CREATE_TASK_MODE.EDIT as any)
                ? translate('common.save')
                : translate('common.create'),
            primary: true,
            variant: 'filled',
            disabled:
              !this.isNameValid(taskName) ||
              (mode === (CREATE_TASK_MODE.EDIT as any) && !this.isTaskNameAndAttributesChanged()),
            onClick: this.onSave.bind(this),
          },
          {
            label: translate('common.cancel'),
            onClick: onClose,
          },
        ]}
      >
        <Stack direction='column' spacing={4}>
          {error && (
            <Alert severity='error' sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
          {mode === (CREATE_TASK_MODE.EDIT as any) || !organization?.enableStandardsLibrary ? (
            <TextField
              value={taskName}
              label={translate('settings.taskNameRequired')}
              dense={true}
              disabled={user === null}
              autoComplete={false}
              autoFocus={true}
              sx={{ width: '100%' }}
              onChange={this.onUpdateName.bind(this)}
              validationMethod={(e?: SyntheticEvent, value?: string) => this.isNameValid(value)}
              validationText={this.handleValidationText(taskName)}
            />
          ) : (
            <Autocomplete
              freeSolo
              id='task-name-input'
              value={taskName || ''}
              options={taskSuggestions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return option?.name || '';
              }}
              renderOption={(props, option, state) => {
                const matches = match(option.name, state.inputValue, {
                  insideWords: true,
                  requireMatchAll: false,
                });
                const parts = parse(option.name, matches);
                return (
                  <li {...props}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Tooltip title={option.name}>
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {parts.map((part, index) => (
                            <span
                              key={index}
                              className={
                                'klayo-selector_itempart' +
                                (part.highlight === true ? ' klayo-selector_highlight' : '')
                              }
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </Tooltip>
                      <i>{translate('addEditAttribute.intelligence')}</i>
                    </Box>
                  </li>
                );
              }}
              disableClearable
              loading={isLoadingTaskSuggestions}
              noOptionsText={translate('common.noOptions')}
              onChange={this.onSelectSuggestion.bind(this)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('settings.taskNameRequired')}
                  dense={true}
                  disabled={user === null}
                  autoComplete={false}
                  autoFocus={true}
                  value={taskName}
                  onChange={(e) => {
                    this.onUpdateName(e);
                    this.debouncedSearchTaskSuggestions(e.target.value);
                  }}
                  sx={{ width: '100%' }}
                  validationMethod={(e?: SyntheticEvent, value?: string) => this.isNameValid(value)}
                  validationText={this.handleValidationText(taskName)}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      type: 'search',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoadingTaskSuggestions ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          )}

          <MultiAttributeSelector<TaskAttribute>
            placeHolder={translate('settings.searchAndAddCompetencies')}
            label={translate('entities.attributes')}
            items={attributesFiltered}
            values={selectedAttributes}
            onNewItemClick={onShowNewAttributeDialog}
            getItemId={(attr) => attr.attributeDefinitionId || ''}
            onChange={this.onAttributesChange.bind(this)}
            onSearch={(searchText: string) => {
              this.onHandleSearchAttributes(searchText);
            }}
            onBlur={this.onAttributesBlur.bind(this)}
            hasNewItem={hasNewItem}
            translate={translate}
            isLoading={this.state.isAttributeLoading}
          />
        </Stack>

        <Fade in={this.state.isOpenImportTaskDialog} unmountOnExit>
          <div>
            <ImportTaskDetailDialog
              theme={theme}
              taskId={this.state.taskId}
              onClose={() => this.setState({ isOpenImportTaskDialog: false })}
              open={this.state.isOpenImportTaskDialog}
              onImport={this.onImportTaskSingle.bind(this)}
              isImporting={this.state.isLoadingImportTask}
              showWarning={true}
            />
          </div>
        </Fade>

        <Fade in={showMessage} unmountOnExit timeout={!this.errorTasks.length ? 3000 : undefined}>
          <div>
            <Snackbar
              open={true}
              duration={!this.errorTasks.length ? 3000 : null}
              type={this.errorTasks.length > 0 ? 'error' : 'success'}
              onClose={() => this.setStateOfMessage(false)}
              message={
                <React.Fragment>
                  <Typography hidden={this.errorTasks.length === 0}>
                    {`${this.errorTasks.length} ${this.errorTasks.length <= 1 ? 'task' : 'tasks'} not imported`}
                  </Typography>
                  <Typography hidden={this.errorTasks.length !== 0}>
                    {`${cacheRows?.importedTasks?.length || 0} ${(cacheRows?.importedTasks?.length || 0) <= 1 ? 'task' : 'tasks'} successfully imported`}
                  </Typography>
                </React.Fragment>
              }
              action={
                <React.Fragment>
                  {!!this.errorTasks.length && (
                    <div
                      style={{
                        marginLeft: 'auto',
                        marginRight: '0.5rem',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                      hidden={this.errorTasks.length === 0}
                      className='klayo-snackbar_text'
                      onClick={this.onImportTasksError.bind(this)}
                    >
                      View Details
                    </div>
                  )}
                  <BlackIconContainer
                    style={{
                      padding: '0',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                    onClick={() => {
                      this.setStateOfMessage(false);
                    }}
                    className='klayo-snackbar_text '
                  >
                    <RemoveIcons />
                  </BlackIconContainer>
                </React.Fragment>
              }
            />
          </div>
        </Fade>

        <ImportTasksErrorDialog
          theme={theme}
          open={this.state.showImportTasksError}
          onClose={this.onCloseImportTasksError.bind(this)}
          tasks={this.errorTasks}
        />
      </Dialog>
    );
  }
}

const BlackIconContainer = styled.div`
  svg {
    path[fill='#1B3DF2'] {
      fill: #000;
    }
  }
`;

export const CreateTaskDialog = withLocale(CreateTaskDialogBase);
